import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import type { StaffAppointment } from "@/types/appointments";
import { format, parseISO } from "date-fns";

type AppointmentVariant = "upcoming" | "ongoing" | "past";

interface AppointmentCardProps {
  appointment: StaffAppointment;
  onClick: () => void;
  variant?: AppointmentVariant;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const variantConfig: Record<
  AppointmentVariant,
  { label: string; className: string }
> = {
  upcoming: {
    label: "Upcoming",
    className: "bg-warning/10 text-warning border-warning/20 dark:text-warning",
  },
  ongoing: {
    label: "Ongoing",
    className: "bg-warning/10 text-warning border-warning/20 dark:text-warning",
  },
  past: {
    label: "Past",
    className: "bg-success/10 text-success border-success/20 dark:text-success",
  },
};

export const AppointmentCard = ({
  appointment,
  onClick,
  variant,
}: AppointmentCardProps) => {
  const formatTime = (dateTimeString: string) => {
    try {
      return format(parseISO(dateTimeString), "hh:mm a");
    } catch {
      return "—";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "EEE, MMM d");
    } catch {
      return "—";
    }
  };

  const slotLabel = `${formatTime(appointment.start_time)} – ${formatTime(appointment.end_time)}`;
  const consultationLabel = appointment.clinic
    ? `In-Person — ${appointment.clinic.name}`
    : appointment.consultation_type.includes("Video")
      ? appointment.consultation_type
      : appointment.location;

  const metaParts = [formatDate(appointment.start_time), slotLabel];
  if (appointment.event_duration) {
    metaParts.push(`${appointment.event_duration} min`);
  }
  if (consultationLabel) {
    metaParts.push(consultationLabel);
  }

  return (
    <Card
      className="p-3 hover:bg-muted/40 transition-colors cursor-pointer border-border"
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left: Patient + meta */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
            <span className="z-body-md font-semibold text-muted-foreground">
              {getInitials(appointment.patient_name)}
            </span>
          </div>
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="z-body-md font-medium text-foreground truncate">
                {appointment.patient_name}
              </h3>
              <Badge
                variant="outline"
                className="font-medium text-muted-foreground border-border shrink-0"
              >
                {appointment.patient_zarity_id}
              </Badge>
            </div>
            <p className="z-secondary text-muted-foreground truncate">
              {metaParts.join(" · ")}
            </p>
          </div>
        </div>

        {/* Right: Status + Appointment ID */}
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {variant && (
            <Badge
              variant="outline"
              className={`font-medium ${variantConfig[variant].className}`}
            >
              {variantConfig[variant].label}
            </Badge>
          )}
          <Badge
            variant="outline"
            className="font-medium text-muted-foreground border-border tabular-nums"
          >
            {appointment.zarity_appointment_id}
          </Badge>
        </div>
      </div>
    </Card>
  );
};
